import React, { useEffect, useState, useCallback } from "react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, Users, Clock, DollarSign, Play, Plus, ChevronRight, Medal, Calendar, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";

export default function Tournaments() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", prize_pool: 0, entry_fee: 0, max_participants: 100 });

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tournaments?status=${filter}`);
      setTournaments(data);
    } catch (e) { console.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { loadTournaments(); }, [loadTournaments]);

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/tournaments/${id}`);
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const joinTournament = async (id) => {
    try {
      await api.post(`/tournaments/${id}/join`);
      toast.success(t("tournament_joined"));
      if (selected?.id === id) loadDetail(id);
      loadTournaments();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const leaveTournament = async (id) => {
    try {
      await api.post(`/tournaments/${id}/leave`);
      toast.success(t("tournament_left"));
      if (selected?.id === id) loadDetail(id);
      loadTournaments();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const createTournament = async () => {
    if (!form.name) return toast.error(t("tournament_name_required"));
    try {
      await api.post("/tournaments", form);
      toast.success(t("tournament_created"));
      setShowCreate(false);
      setForm({ name: "", description: "", prize_pool: 0, entry_fee: 0, max_participants: 100 });
      loadTournaments();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <Trophy className="w-7 h-7 text-yellow-500" /> {t("tournament_title")}
          </h1>
          <button onClick={() => setShowCreate(!showCreate)} className="bg-buy text-black px-4 py-2 rounded-sm text-sm font-bold hover:bg-emerald-400 flex items-center gap-2">
            <Plus className="w-4 h-4" /> {t("tournament_new")}
          </button>
        </div>

        {/* Create Form */}
        {showCreate && (
          <div className="bg-panel border border-line rounded-sm p-5 mb-6 space-y-4">
            <h2 className="text-sm font-bold tracking-widest uppercase text-buy">{t("tournament_create")}</h2>
            <input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder={t("tournament_name_placeholder")} className="w-full bg-app border border-line px-3 py-2 rounded-sm text-sm outline-none focus:border-buy" />
            <input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder={t("tournament_desc_placeholder")} className="w-full bg-app border border-line px-3 py-2 rounded-sm text-sm outline-none focus:border-buy" />
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-dim block mb-1">{t("tournament_prize_pool")}</label>
                <input type="number" value={form.prize_pool} onChange={(e) => setForm(p => ({ ...p, prize_pool: Number(e.target.value) }))}
                  className="w-full bg-app border border-line px-3 py-2 rounded-sm text-sm outline-none focus:border-buy font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-dim block mb-1">{t("tournament_entry_fee")}</label>
                <input type="number" value={form.entry_fee} onChange={(e) => setForm(p => ({ ...p, entry_fee: Number(e.target.value) }))}
                  className="w-full bg-app border border-line px-3 py-2 rounded-sm text-sm outline-none focus:border-buy font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-dim block mb-1">{t("tournament_max_participants")}</label>
                <input type="number" value={form.max_participants} onChange={(e) => setForm(p => ({ ...p, max_participants: Number(e.target.value) }))}
                  className="w-full bg-app border border-line px-3 py-2 rounded-sm text-sm outline-none focus:border-buy font-mono" />
              </div>
            </div>
            <button onClick={createTournament} className="bg-buy text-black px-4 py-2 rounded-sm text-sm font-bold hover:bg-emerald-400">{t("tournament_create_button")}</button>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {["upcoming", "active", "ended"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-2 text-xs uppercase font-bold rounded-sm border ${filter === f ? "border-buy bg-buy/10 text-buy" : "border-line text-dim hover:bg-panel-hover"}`}>
              {f === "upcoming" ? t("tournament_upcoming") : f === "active" ? t("tournament_active") : t("tournament_ended")}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tournament List */}
          <div className="space-y-4">
            {loading ? (
              <div className="text-dim text-sm py-8 text-center">{t("loading")}</div>
            ) : tournaments.length === 0 ? (
              <div className="text-dim text-sm py-8 text-center">{t("tournament_no_data")}</div>
            ) : tournaments.map((t) => (
              <div key={t.id} onClick={() => loadDetail(t.id)}
                className="bg-panel border border-line rounded-sm p-4 cursor-pointer hover:border-buy/50 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-yellow-500" /> {t.name}
                  </h3>
                  <ChevronRight className="w-4 h-4 text-dim" />
                </div>
                <div className="flex items-center gap-4 text-xs text-dim">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {t.participants}/{t.max_participants}</span>
                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${t.prize_pool}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(t.start_date).toLocaleDateString("tr-TR")}</span>
                </div>
                {t.entry_fee > 0 && (
                  <span className="text-[10px] text-orange-400 mt-1">{t("tournament_entry_label")}: ${t.entry_fee}</span>
                )}
              </div>
            ))}
          </div>

          {/* Tournament Detail */}
          {selected && (
            <div className="bg-panel border border-line rounded-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">{selected.name}</h2>
                <button onClick={() => setSelected(null)} className="text-dim text-xs hover:text-white">{t("tournament_close")}</button>
              </div>
              <p className="text-xs text-dim mb-4">{selected.description}</p>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-app rounded-sm p-3 text-center">
                  <div className="text-[10px] text-dim uppercase tracking-widest">{t("tournament_prize")}</div>
                  <div className="text-lg font-bold text-yellow-500 font-mono">${selected.prize_pool}</div>
                </div>
                <div className="bg-app rounded-sm p-3 text-center">
                  <div className="text-[10px] text-dim uppercase tracking-widest">{t("tournament_participants")}</div>
                  <div className="text-lg font-bold font-mono">{selected.leaderboard.length}</div>
                </div>
                <div className="bg-app rounded-sm p-3 text-center">
                  <div className="text-[10px] text-dim uppercase tracking-widest">{t("tournament_mode")}</div>
                  <div className="text-lg font-bold font-mono uppercase">{selected.mode}</div>
                </div>
              </div>

              <button onClick={() => joinTournament(selected.id)}
                className="w-full bg-buy text-black py-2.5 rounded-sm text-sm font-bold hover:bg-emerald-400 mb-4 flex items-center justify-center gap-2">
                <UserPlus className="w-4 h-4" /> {t("tournament_join")}
              </button>

              {/* Leaderboard */}
              <h3 className="text-xs font-bold uppercase tracking-widest text-dim mb-2">{t("tournament_ranking")}</h3>
              {selected.leaderboard.length === 0 ? (
                <div className="text-dim text-xs py-4">{t("tournament_no_participants")}</div>
              ) : (
                <div className="space-y-1.5">
                  {selected.leaderboard.slice(0, 20).map((p) => (
                    <div key={p.user_id} className="flex items-center gap-3 bg-app border border-subtle/30 rounded-sm px-3 py-2">
                      <span className={`font-mono text-xs font-bold w-6 text-center ${p.rank <= 3 ? "text-yellow-500" : "text-dim"}`}>
                        {p.rank <= 3 ? <Medal className="w-4 h-4 inline" /> : `#${p.rank}`}
                      </span>
                      <span className="flex-1 text-xs font-bold truncate">{p.name}</span>
                      <span className={`font-mono text-xs ${p.pnl >= 0 ? "text-buy" : "text-sell"}`}>
                        {p.pnl >= 0 ? "+" : ""}${p.pnl.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
